import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Phone, Menu, X, ShieldCheck } from "lucide-react";
import { BUSINESS, SERVICES, CITIES } from "./constants";

export function Header() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [areasOpen, setAreasOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 bg-charcoal text-brand-foreground backdrop-blur border-b-2 border-cta/70">
      <div className="hidden md:flex bg-black text-brand-foreground text-xs border-b border-white/10">
        <div className="mx-auto max-w-7xl w-full px-4 py-1.5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-cta">
            <ShieldCheck className="size-3.5" />
            <span className="font-semibold tracking-wider uppercase">Licensed & Insured · {BUSINESS.license}</span>
          </div>
          <div className="opacity-80 uppercase tracking-wider">{BUSINESS.serviceArea}</div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <div className="size-11 rounded-md bg-cta text-black grid place-items-center font-display font-bold text-2xl border border-leather">
            D
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-xl text-cta tracking-wider uppercase">Destination Roofing</div>
            <div className="text-[11px] text-brand-foreground/60 -mt-0.5 uppercase tracking-widest">Citrus County, FL</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 text-sm font-semibold uppercase tracking-wider" aria-label="Primary">
          <Link to="/" className="px-3 py-2 text-brand-foreground hover:text-cta">Home</Link>
          <div className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
            <Link to="/services" className="px-3 py-2 text-brand-foreground hover:text-cta inline-block">Services</Link>
            {servicesOpen && (
              <div className="absolute left-0 top-full pt-2 w-64">
                <div className="bg-charcoal rounded-lg shadow-xl border border-cta/40 py-2">
                  {SERVICES.map(s => (
                    <Link key={s.slug} to={s.slug} className="block px-4 py-2 text-brand-foreground hover:bg-black hover:text-cta text-sm">{s.title}</Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="relative" onMouseEnter={() => setAreasOpen(true)} onMouseLeave={() => setAreasOpen(false)}>
            <Link to="/service-areas" className="px-3 py-2 text-brand-foreground hover:text-cta inline-block">Service Areas</Link>
            {areasOpen && (
              <div className="absolute left-0 top-full pt-2 w-64">
                <div className="bg-charcoal rounded-lg shadow-xl border border-cta/40 py-2">
                  {CITIES.map(c => (
                    <Link key={c.slug} to={c.slug} className="block px-4 py-2 text-brand-foreground hover:bg-black hover:text-cta text-sm">{c.city}, {c.state}</Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <Link to="/blog" className="px-3 py-2 text-brand-foreground hover:text-cta">Blog</Link>
          <Link to="/atlas-shingle-colors" className="px-3 py-2 text-brand-foreground hover:text-cta">Roof Colors</Link>
          <Link to="/gallery" className="px-3 py-2 text-brand-foreground hover:text-cta">Gallery</Link>
          <Link to="/reviews" className="px-3 py-2 text-brand-foreground hover:text-cta">Reviews</Link>
          <Link to="/about" className="px-3 py-2 text-brand-foreground hover:text-cta">About</Link>
          <Link to="/contact" className="px-3 py-2 text-brand-foreground hover:text-cta">Contact</Link>
        </nav>

        <div className="flex items-center gap-2">
          <a href={BUSINESS.phoneHref} className="hidden sm:inline-flex items-center gap-2 btn-cta text-sm !py-2 !min-h-11">
            <Phone className="size-4" /> {BUSINESS.phone}
          </a>
          <button
            aria-label="Menu"
            onClick={() => setOpen(!open)}
            className="lg:hidden size-11 grid place-items-center rounded-md border border-cta/50 text-cta"
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-cta/40 bg-charcoal">
          <div className="px-4 py-3 space-y-1 max-h-[75vh] overflow-y-auto">
            <MobileLink to="/" onClick={() => setOpen(false)}>Home</MobileLink>
            <MobileLink to="/services" onClick={() => setOpen(false)}>All Services</MobileLink>
            <div className="pt-2 pb-1 text-xs font-bold text-cta uppercase tracking-widest">Services</div>
            {SERVICES.map(s => <MobileLink key={s.slug} to={s.slug} onClick={() => setOpen(false)}>{s.title}</MobileLink>)}
            <MobileLink to="/service-areas" onClick={() => setOpen(false)}>All Service Areas</MobileLink>
            <div className="pt-2 pb-1 text-xs font-bold text-cta uppercase tracking-widest">Service Areas</div>
            {CITIES.map(c => <MobileLink key={c.slug} to={c.slug} onClick={() => setOpen(false)}>{c.city}, {c.state}</MobileLink>)}
            <div className="pt-2 border-t border-white/10 mt-2">
              <MobileLink to="/blog" onClick={() => setOpen(false)}>Blog</MobileLink>
              <MobileLink to="/atlas-shingle-colors" onClick={() => setOpen(false)}>Roof Color Visualizer</MobileLink>
              <MobileLink to="/gallery" onClick={() => setOpen(false)}>Gallery</MobileLink>
              <MobileLink to="/reviews" onClick={() => setOpen(false)}>Reviews</MobileLink>
              <MobileLink to="/about" onClick={() => setOpen(false)}>About</MobileLink>
              <MobileLink to="/contact" onClick={() => setOpen(false)}>Contact</MobileLink>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function MobileLink({ to, children, onClick }: { to: string; children: React.ReactNode; onClick: () => void }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="block px-3 py-3 rounded-md text-brand-foreground hover:bg-black hover:text-cta text-base font-semibold uppercase tracking-wider"
    >
      {children}
    </Link>
  );
}
